import { useResendInvitationMutation } from '@/api/api.slice.tsx';
import { openDialogCancelInvite } from '@/features/accounts/accounts.slice.ts';
import { AccountType } from '@/features/accounts/types.ts';
import { useConnectToUserAccount } from '@/features/accounts/useConnectToUserAccount.ts';
import i18n from '@/i18n/i18n.ts';
import {
  Alert,
  Avatar,
  Button,
  Card,
  CardField,
} from '@arsnovasystems/ars-front-lib';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

/**
 * Card component, dedicated to the rendering of the Customer Account
 *
 * @param props         props of the component
 * @param props.account Contains all the data to be displayed in a card
 *
 * @returns             JSX of the component
 */
export const CustomerCard = ({ account }: { account: AccountType }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [resendInvitation, { isLoading: resendInvitationIsLoading }] =
    useResendInvitationMutation();

  const onClickConnect = useConnectToUserAccount({
    userPublicId: account.id,
  });

  return (
    <Card
      bold
      color={account.isInvitation ? 'neutral' : 'primary'}
      Avatar={
        <Avatar colorKey={account.email}>
          {account.companyName ?
            account.companyName.slice(0, 2)
          : account.email.slice(0, 2)}
        </Avatar>
      }
      title={account.companyName}
      subtitle={account.email}
      cardActions={[
        ...(!account.isInvitation ?
          [
            <Button
              variant='contained'
              onClick={onClickConnect}
            >
              {i18n.t('ik.connect')}
            </Button>,
          ]
        : []),
        ...(account.isInvitation ?
          [
            <Button
              color={'secondary'}
              variant={'outlined'}
              onClick={() => {
                dispatch(openDialogCancelInvite(account));
              }}
            >
              {t('Cancel')}
            </Button>,
            <Button
              loading={resendInvitationIsLoading}
              onClick={() => {
                void resendInvitation({
                  email: account.email,
                  companyName: account.companyName,
                  contactName: account.contactName,
                });
              }}
            >
              {i18n.t('ik.resend.invitation')}
            </Button>,
          ]
        : []),
      ]}
    >
      <CardField
        label={t('ik.contact')}
        value={account.contactName}
      />
      <CardField
        label={t('ik.licences')}
        value={account.licencesCount.toString()}
      />
      {account.endSubscriptionDate && (
        <CardField
          label={t('ik.customerAccounts.card.subscriptionEnd')}
          value={account.endSubscriptionDate}
        />
      )}
      {!account.endSubscriptionDate && account.endOfTrialDate && (
        <CardField
          label={t('ik.customerAccounts.card.trialEnd')}
          value={account.endOfTrialDate}
        />
      )}
      {account.isInvitation ?
        <Alert
          compact
          sx={{ marginTop: 2 }}
          severity='info'
          variant={'standard'}
        >
          {t('ik.account.info.invitation')}
        </Alert>
      : null}
    </Card>
  );
};
